import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import type { DrinkDetail } from "../types/drink";
import type { PrepListItem } from "../lib/api";
import IngredientList from "./IngredientList";
import FinishStepsList from "./FinishStepsList";
import MultiplierControl from "./MultiplierControl";
import IssueModal from "./IssueModal";
import { useConfirmDialog } from "./ConfirmDialog";

interface Props {
  detail: DrinkDetail;
  cocktailCount: number;
  checkedIngredients: Record<string, boolean>;
  checkedFinishSteps: Record<string, boolean>;
  onSetCount: (n: number) => void;
  onToggleIngredient: (ingredientId: string) => void;
  onToggleFinishStep: (finishStepId: string) => void;
  onResetCocktail: () => void;
  onClearChecks: () => void;
  onBack: () => void;
  /** First name of the logged-in customer (defaults the IssueModal station_label). */
  reporterName?: string | null;
  /** All Active+Approved preps, fetched once at Station mount. We filter to
   * the ones referenced by THIS drink to render summary info inline. */
  preps?: PrepListItem[];
  /** Optional callback fired when the bartender flags an ingredient out.
   * Parent (Station.tsx) typically refreshes the active drink so the row
   * paints with the unavailable flag. */
  onIngredientFlagged?: () => void;
  /** Optional callback when the bartender taps "Drink complete" at the
   * bottom — remove this drink from the loaded tray so the active recipe
   * list stays current. */
  onCompleteDrink?: () => void;
}

export default function RecipeDetail({
  detail,
  cocktailCount,
  checkedIngredients,
  checkedFinishSteps,
  onSetCount,
  onToggleIngredient,
  onToggleFinishStep,
  onResetCocktail,
  onClearChecks,
  onBack,
  reporterName,
  preps,
  onIngredientFlagged,
  onCompleteDrink,
}: Props) {
  const { drink, version, ingredients, finish_steps, has_unavailable_ingredient } = detail;
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueFlash, setIssueFlash] = useState<string | null>(null);
  const [selectedPrep, setSelectedPrep] = useState<PrepListItem | null>(null);
  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  // Track flash timeouts so unmount + setState-after-unmount doesn't warn,
  // and a new flash doesn't get cleared early by a stale older timer.
  const flashTimerRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
  }, []);
  function flashIssue(msg: string, ms: number) {
    setIssueFlash(msg);
    if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    flashTimerRef.current = window.setTimeout(() => setIssueFlash(null), ms);
  }

  // Filter the cached prep list down to only the ones referenced by this drink's
  // ingredients OR finish_steps. Empty result hides the section entirely.
  const linkedPreps = useMemo(() => {
    if (!preps || preps.length === 0) return [];
    const referenced = new Set<string>();
    for (const ing of ingredients) {
      if (ing.prep_recipe_id) referenced.add(ing.prep_recipe_id);
    }
    for (const fs of finish_steps) {
      if (fs.prep_recipe_id) referenced.add(fs.prep_recipe_id);
    }
    if (referenced.size === 0) return [];
    return preps.filter((p) => referenced.has(p.id));
  }, [preps, ingredients, finish_steps]);

  if (!version) {
    return (
      <div style={styles.empty}>
        <button type="button" onClick={onBack} style={styles.backLink}>
          ← Back
        </button>
        <p style={styles.muted}>This recipe has no approved version yet.</p>
      </div>
    );
  }

  const showFeatured = drink.is_featured_today;
  const showUpdated = version.is_recently_updated;
  const show86 = drink.service_status === "86d" || has_unavailable_ingredient;
  // Date for the "Updated" chip — shows e.g. "Updated 5-1-2026" (M-D-YYYY).
  // Falls back to bare "Updated" if approved_at is missing/unparseable.
  const updatedDate = (() => {
    if (!version.approved_at) return "";
    const d = new Date(version.approved_at);
    if (Number.isNaN(d.getTime())) return "";
    return `${d.getMonth() + 1}-${d.getDate()}-${d.getFullYear()}`;
  })();

  return (
    <article style={styles.card}>
      <header style={styles.header}>
        <h2 style={styles.name}>{drink.name}</h2>
        <div style={styles.chips}>
          <Chip tone="approved" label={`✓ ${version.status}`} />
          <Chip tone="muted" label={`v${version.version_number}`} />
          <Chip tone="muted" label="🔒 Locked" />
          {showUpdated && (
            <Chip tone="blue" label={updatedDate ? `Updated ${updatedDate}` : "Updated"} />
          )}
          {showFeatured && <Chip tone="gold" label="Featured Today" />}
          {show86 && <Chip tone="red" label="86" />}
        </div>
        {drink.tags?.length > 0 && (
          <p style={styles.tags}>{drink.tags.join(" · ")}</p>
        )}
      </header>

      {version.warning && (
        <div style={styles.warning}>
          <span aria-hidden style={{ marginRight: 8 }}>⚠</span>
          {version.warning}
        </div>
      )}

      <div style={styles.metaGrid}>
        <Meta label="Glass" value={version.glass} />
        <Meta label="Ice" value={version.ice} />
        <Meta label="Method" value={version.method} />
        <Meta label="Garnish" value={version.garnish} />
      </div>

      <Section title="Ingredients">
        <MultiplierControl
          count={cocktailCount}
          onChange={onSetCount}
          onResetCocktail={onResetCocktail}
          onClearChecks={onClearChecks}
        />
        <IngredientList
          ingredients={ingredients}
          cocktailCount={cocktailCount}
          checked={checkedIngredients}
          onToggle={onToggleIngredient}
          onFlashMessage={(msg) => flashIssue(msg, 5000)}
          onIngredientFlagged={onIngredientFlagged}
        />
      </Section>

      {version.staff_notes && (
        <Section title="Staff Note">
          <p style={styles.notesText}>{version.staff_notes}</p>
        </Section>
      )}

      {/* Build card always renders so the Finish subsection inside it is
          guaranteed to be visible on every drink, even those with no
          structured build/finish data yet. */}
      <Section title="Build">
        {version.instructions.length > 0 ? (
          <ol style={styles.steps}>
            {version.instructions.map((step, i) => (
              <li key={i} style={styles.step}>
                <span style={styles.stepNum}>{i + 1}</span>
                <span style={styles.stepText}>{step}</span>
              </li>
            ))}
          </ol>
        ) : (
          <p style={styles.subsectionEmpty}>
            No build steps recorded for this version yet.
          </p>
        )}

        {/* Finish subsection — ALWAYS rendered so the bartender sees the
            structural divider between building and finishing/garnishing/
            smoking. When finish_steps is empty, show a muted hint that
            points the manager at where to populate it (recipe editor §F).
            Drinks where the finish instructions are still bundled into
            the build steps above will hit the empty branch — that's a
            data migration the manager can do later. */}
        <h4 style={styles.subsectionTitle}>Finish</h4>
        {finish_steps.length > 0 ? (
          <FinishStepsList
            finishSteps={finish_steps}
            cocktailCount={cocktailCount}
            checked={checkedFinishSteps}
            onToggle={onToggleFinishStep}
          />
        ) : (
          <p style={styles.subsectionEmpty}>
            No separate finish steps recorded — finishing / garnishing /
            smoking instructions may be in the build steps above. Manager
            can add structured finish steps in the recipe editor.
          </p>
        )}
      </Section>

      {linkedPreps.length > 0 && (
        <Section title="Prep recipes used">
          <ul style={styles.prepList}>
            {linkedPreps.map((p) => (
              <li
                key={p.id}
                style={styles.prepRow}
                onClick={() => setSelectedPrep(p)}
              >
                <div style={styles.prepName}>
                  <strong>{p.name}</strong>
                  <span style={styles.prepCategory}>{p.category}</span>
                </div>
                <div style={styles.prepMetaRow}>
                  {p.approved_version?.yield_amount != null && (
                    <span style={styles.prepMeta}>
                      Yield: {p.approved_version.yield_amount}
                      {p.approved_version.yield_unit ? ` ${p.approved_version.yield_unit}` : ""}
                    </span>
                  )}
                  {p.approved_version?.shelf_life_days != null && (
                    <span style={styles.prepMeta}>
                      Shelf: {p.approved_version.shelf_life_days}d
                    </span>
                  )}
                  {p.approved_version?.storage && (
                    <span style={styles.prepMeta}>{p.approved_version.storage}</span>
                  )}
                  {p.approved_version?.version_number && (
                    <span style={styles.prepVersion}>v{p.approved_version.version_number}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <div style={styles.reportRow}>
        {issueFlash && <span style={styles.flash} role="status">{issueFlash}</span>}
        <button
          type="button"
          onClick={() => {
            setIssueFlash(null);
            setIssueOpen(true);
          }}
          style={styles.reportBtn}
        >
          Report issue
        </button>
      </div>

      <div style={styles.bottomActions}>
        {/* Scroll-to-top — recipes can run long with multiple sections; bartenders
            working multiple drinks at once want fast access to the top of THIS
            recipe (so the multiplier control is visible) without scrolling
            back manually. */}
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          style={styles.scrollTopBtn}
        >
          ↑ Top
        </button>
        {/* Drink complete — clears this drink from the loaded tray so the
            "active drinks" list stays current. Different from Reset Cocktail
            (which resets the multiplier+checks) and End Round (which clears
            the whole tray). Hidden if onCompleteDrink isn't wired.
            Confirms first because tapjacking on a tablet floor would be
            catastrophic — bartender loses all their checked ingredients
            with one accidental tap (Codex security review L33 / P2). */}
        {onCompleteDrink && (
          <button
            type="button"
            onClick={async () => {
              const ok = await confirm({
                title: "Mark drink complete?",
                message: `Remove "${drink.name}" from your loaded drinks. Your other loaded drinks aren't affected.`,
                confirmLabel: "Complete",
              });
              if (ok) onCompleteDrink();
            }}
            style={styles.completeBtn}
          >
            ✓ Drink complete
          </button>
        )}
      </div>
      {confirmDialog}

      {/* ⚡ PREP RECIPE QUICK VIEW MODAL DRAWER */}
      {selectedPrep && (
        <div style={styles.modalBackdrop} onClick={() => setSelectedPrep(null)}>
          <div style={styles.modalDialog} onClick={(e) => e.stopPropagation()}>
            <header style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>{selectedPrep.name}</h3>
              <button type="button" style={styles.modalCloseBtn} onClick={() => setSelectedPrep(null)}>✕</button>
            </header>
            
            <div style={styles.modalMetaGrid}>
              <div style={styles.modalMetaCard}>
                <span style={styles.modalMetaLabel}>Category</span>
                <span style={styles.modalMetaValue}>{selectedPrep.category}</span>
              </div>
              {selectedPrep.approved_version?.yield_amount != null && (
                <div style={styles.modalMetaCard}>
                  <span style={styles.modalMetaLabel}>Batch Yield</span>
                  <span style={styles.modalMetaValue}>
                    {selectedPrep.approved_version.yield_amount} {selectedPrep.approved_version.yield_unit || ""}
                  </span>
                </div>
              )}
              {selectedPrep.approved_version?.shelf_life_days != null && (
                <div style={styles.modalMetaCard}>
                  <span style={styles.modalMetaLabel}>Shelf Life</span>
                  <span style={styles.modalMetaValue}>{selectedPrep.approved_version.shelf_life_days} days</span>
                </div>
              )}
              {selectedPrep.approved_version?.storage && (
                <div style={styles.modalMetaCard}>
                  <span style={styles.modalMetaLabel}>Storage</span>
                  <span style={styles.modalMetaValue}>{selectedPrep.approved_version.storage}</span>
                </div>
              )}
            </div>

            {selectedPrep.approved_version?.ingredients_text && (
              <div style={styles.modalSection}>
                <h4 style={styles.modalSectionTitle}>Ingredients</h4>
                <p style={styles.modalPreText}>{selectedPrep.approved_version.ingredients_text}</p>
              </div>
            )}

            {selectedPrep.approved_version?.build_instructions && (
              <div style={styles.modalSection}>
                <h4 style={styles.modalSectionTitle}>Build Instructions</h4>
                <p style={styles.modalPreText}>{selectedPrep.approved_version.build_instructions}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <IssueModal
        open={issueOpen}
        drinkId={drink.id}
        drinkName={drink.name}
        versionLabel={version ? `v${version.version_number}` : null}
        defaultStationLabel={reporterName ?? null}
        onClose={() => setIssueOpen(false)}
        onSubmitted={(msg) => flashIssue(msg, 4000)}
      />
    </article>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      {children}
    </section>
  );
}

function Meta({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div style={styles.metaBox}>
      <p style={styles.metaLabel}>{label}</p>
      <p style={styles.metaValue}>{value ?? "—"}</p>
    </div>
  );
}

function Chip({
  label,
  tone,
}: {
  label: string;
  tone: "gold" | "blue" | "red" | "muted" | "approved";
}) {
  return <span style={{ ...styles.chip, ...chipTone[tone] }}>{label}</span>;
}

const chipTone: Record<string, CSSProperties> = {
  gold:     { background: "rgba(249,175,54,0.12)", color: "var(--gold)", borderColor: "rgba(249,175,54,0.3)" },
  blue:     { background: "rgba(96,165,250,0.12)", color: "#93c5fd",     borderColor: "rgba(96,165,250,0.3)" },
  red:      { background: "rgba(224,80,64,0.16)",  color: "#FF8A7A",     borderColor: "rgba(224,80,64,0.4)"  },
  muted:    { background: "var(--bg)",             color: "var(--muted)",borderColor: "var(--border)"        },
  approved: { background: "rgba(39,174,96,0.16)",  color: "#86efac",     borderColor: "rgba(39,174,96,0.3)"  },
};

const styles: Record<string, CSSProperties> = {
  card: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 16,
    padding: 16,
    marginTop: 12,
  },
  backLink: {
    background: "transparent",
    border: 0,
    color: "var(--muted)",
    fontWeight: 700,
    fontSize: 13,
    padding: 0,
    marginBottom: 12,
    cursor: "pointer",
  },
  header: { marginBottom: 12 },
  chips: { display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 8px",
    borderRadius: 999,
    border: "1px solid",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  name: {
    fontFamily: "Georgia, serif",
    margin: "4px 0 6px",
    fontSize: 28,
    letterSpacing: -0.4,
    color: "var(--text)",
  },
  tags: { margin: 0, fontSize: 12, color: "var(--muted)", fontWeight: 600 },
  warning: {
    display: "flex",
    alignItems: "flex-start",
    gap: 4,
    padding: 10,
    borderRadius: 12,
    background: "rgba(249,175,54,0.08)",
    border: "1px solid rgba(249,175,54,0.3)",
    color: "var(--gold)",
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.35,
    marginBottom: 12,
  },
  metaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 6,
    marginBottom: 12,
  },
  metaBox: {
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    padding: 10,
  },
  metaLabel: {
    margin: 0,
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--muted)",
  },
  metaValue: {
    margin: "4px 0 0",
    fontSize: 13,
    fontWeight: 800,
    color: "var(--text)",
    lineHeight: 1.25,
  },
  section: {
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    padding: 12,
    marginBottom: 10,
  },
  sectionTitle: {
    margin: "0 0 10px",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--muted)",
  },
  steps: { listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 },
  step: {
    display: "grid",
    gridTemplateColumns: "28px 1fr",
    gap: 10,
    padding: 10,
    borderRadius: 10,
    background: "var(--surface)",
  },
  stepNum: {
    width: 24,
    height: 24,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    background: "var(--gold)",
    color: "#0D0B08",
    fontWeight: 800,
    fontSize: 12,
  },
  stepText: { fontSize: 14, lineHeight: 1.3, color: "var(--text)", fontWeight: 600 },
  notesText: { margin: 0, fontSize: 13, lineHeight: 1.4, color: "var(--text)" },
  prepList: { listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 },
  prepRow: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 10,
    cursor: "pointer",
  },
  prepName: { display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" },
  prepCategory: {
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "var(--muted)",
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 999,
    padding: "1px 6px",
  },
  prepMetaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 6,
  },
  prepMeta: {
    fontSize: 12,
    color: "var(--text)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  },
  prepVersion: {
    fontSize: 11,
    color: "var(--muted)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    marginLeft: "auto",
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.75)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 99,
  },
  modalDialog: {
    width: "100%",
    maxWidth: 500,
    maxHeight: "85vh",
    overflowY: "auto",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 16,
    padding: 24,
    boxShadow: "0 12px 36px rgba(0,0,0,0.6)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
    borderBottom: "1px solid var(--border)",
    paddingBottom: 12,
  },
  modalTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 20,
    margin: 0,
    color: "var(--text)",
  },
  modalCloseBtn: {
    background: "transparent",
    border: 0,
    color: "var(--muted)",
    fontSize: 16,
    cursor: "pointer",
    padding: 4,
  },
  modalMetaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 12,
    marginBottom: 20,
  },
  modalMetaCard: {
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 10,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  modalMetaLabel: {
    fontSize: 10,
    fontWeight: 800,
    color: "var(--muted)",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  modalMetaValue: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--text)",
  },
  modalSection: {
    marginBottom: 16,
    borderTop: "1px solid var(--border)",
    paddingTop: 12,
  },
  modalSectionTitle: {
    fontSize: 11,
    fontWeight: 800,
    color: "var(--muted)",
    textTransform: "uppercase",
    letterSpacing: 0.6,
    margin: "0 0 8px",
  },
  modalPreText: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.5,
    color: "var(--text)",
    whiteSpace: "pre-line",
  },
  reportRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginTop: 16,
    paddingTop: 12,
    borderTop: "1px solid var(--border)",
  },
  flash: {
    fontSize: 12,
    color: "#86efac",
    fontWeight: 700,
    background: "rgba(39,174,96,0.12)",
    border: "1px solid rgba(39,174,96,0.3)",
    borderRadius: 8,
    padding: "6px 10px",
  },
  reportBtn: {
    background: "transparent",
    color: "var(--muted)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "10px 16px",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    cursor: "pointer",
    minHeight: 40,
    marginLeft: "auto",
  },
  // Bottom action row — Top + Drink complete. Mobile-responsive: wraps to
  // stacked buttons on narrow screens. Both buttons are full ≥44px touch
  // targets per WCAG 2.5.5.
  bottomActions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 16,
    paddingTop: 12,
    borderTop: "1px solid var(--border)",
  },
  scrollTopBtn: {
    flex: "1 1 140px",
    background: "transparent",
    color: "var(--muted)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "12px 16px",
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    cursor: "pointer",
    minHeight: 44,
  },
  completeBtn: {
    flex: "2 1 200px",
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 10,
    padding: "12px 16px",
    fontSize: 13,
    fontWeight: 900,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    cursor: "pointer",
    minHeight: 44,
  },
  // Subsection heading inside the merged Build card (used to label the
  // "Finish" sublist after the build steps). Matches the visual weight of
  // sectionTitle but slightly smaller, with a hairline divider above so the
  // bartender's eye knows the build instructions ended.
  subsectionTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 16,
    fontWeight: 700,
    margin: "16px 0 8px",
    paddingTop: 12,
    borderTop: "1px solid var(--border)",
    color: "var(--text)",
    letterSpacing: -0.2,
  },
  // Empty-state line under a subsection heading (e.g. "Finish" with no
  // finish_steps yet). Muted so it doesn't compete visually with real
  // build steps; small enough that the bartender knows to look up.
  subsectionEmpty: {
    fontSize: 12,
    color: "var(--muted)",
    fontStyle: "italic",
    lineHeight: 1.4,
    margin: "0 0 4px",
  },
  empty: { padding: 24 },
  muted: { color: "var(--muted)", fontSize: 14 },
};
